import React, { useState, useEffect } from 'react';
import axios from 'axios';
import "../../Styles/FeeTransaction.css";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import ViewListIcon from "@mui/icons-material/ViewList";
import DownloadIcon from "@mui/icons-material/Download";
import DateRangeIcon from "@mui/icons-material/DateRange";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import Loader from '../../Components/Loader';

function FeeTransaction() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    receiptNo: '',
    startDate: '',
    endDate: '',
    financialYear: '',
    paymentMode: '',
    degree: ''
  });

  // Fetch receipts data
  useEffect(() => {
    const fetchReceipts = async () => {
      setLoading(true);
      try {
        // Build query params
        const params = new URLSearchParams();
        params.append('page', currentPage);
        params.append('limit', 10);
        
        // Add filters if they exist
        Object.entries(filters).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });
        
        // Get auth token, make sure it matches what your backend expects
        const token = localStorage.getItem('authToken');
        
        // Log the user info being sent
        console.log("Making API request with token:", token);
        
        const response = await axios.get(`https://api.uniums.com/api/bill/student-receipts?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        console.log("API Response:", response.data);
        
        if (response.data && response.data.receipts) {
          setReceipts(response.data.receipts);
          setTotalAmount(response.data.summary.totalAmount);
          setTotalPages(response.data.pagination.pages);
        } else {
          console.error("Invalid response format:", response.data);
          setError('Received invalid data format from server');
        }
      } catch (err) {
        console.error("Error fetching receipts:", err);
        // More detailed error handling
        if (err.response) {
          console.error("Response status:", err.response.status);
          console.error("Response data:", err.response.data);
          setError(`Failed to fetch receipts: ${err.response.data.message || 'Server error'}`);
        } else if (err.request) {
          console.error("No response received:", err.request);
          setError('No response received from server. Please check your connection.');
        } else {
          console.error("Error:", err.message);
          setError(`Error: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReceipts();
  }, [currentPage, filters]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  // Apply filters
  const applyFilters = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when applying filters
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      receiptNo: '',
      startDate: '',
      endDate: '',
      financialYear: '',
      paymentMode: '',
      degree: ''
    });
    setCurrentPage(1);
  };

  // Handle pagination
  const changePage = (page) => {
    setCurrentPage(page);
  };

  // Render pagination buttons
  const renderPagination = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => changePage(i)}
          className={`student-fee-pagination-button ${currentPage === i ? 'student-fee-active-page' : ''}`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  // Download PDF
  const downloadPdf = (pdfUrl, studentName, receiptNo) => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `Receipt_${receiptNo}_${studentName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Format date function
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="student-fee-container">
      <div className="student-fee-header">
        <h1 className="student-fee-title">Fee Transaction History</h1>
        <div className="student-fee-summary">
          <div className="student-fee-total-box">
            <SubscriptionsIcon className="student-fee-icon" />
            <span className="student-fee-total-label">Total Paid</span>
            <span className="student-fee-total-amount">₹{totalAmount.toLocaleString()}</span>
          </div>
          <button
            className="student-fee-filter-button"
            onClick={() => setShowFilters(!showFilters)}
          >
            <ViewListIcon className="student-fee-icon" /> Filter
          </button>
        </div>
      </div>

      {/* Filter Section */}
      {showFilters && (
        <div className="student-fee-filter-container">
          <form onSubmit={applyFilters} className="student-fee-filter-form">
            <div className="student-fee-filter-group">
              <label className="student-fee-filter-label">
                Receipt Number
                <input
                  type="text"
                  name="receiptNo"
                  value={filters.receiptNo}
                  onChange={handleFilterChange}
                  className="student-fee-filter-input"
                  placeholder="Enter receipt number"
                />
              </label>
            </div>

            <div className="student-fee-filter-group">
              <label className="student-fee-filter-label">
                <DateRangeIcon className="student-fee-icon" /> From Date
                <input
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  className="student-fee-filter-input"
                />
              </label>
            </div>

            <div className="student-fee-filter-group">
              <label className="student-fee-filter-label">
                <DateRangeIcon className="student-fee-icon" /> To Date
                <input
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  className="student-fee-filter-input"
                />
              </label>
            </div>

            <div className="student-fee-filter-group">
              <label className="student-fee-filter-label">
                Financial Year
                <input
                  type="text"
                  name="financialYear"
                  value={filters.financialYear}
                  onChange={handleFilterChange}
                  className="student-fee-filter-input"
                  placeholder="e.g. 2025"
                />
              </label>
            </div>

            <div className="student-fee-filter-group">
              <label className="student-fee-filter-label">
                <AttachMoneyIcon className="student-fee-icon" /> Payment Mode
                <select
                  name="paymentMode"
                  value={filters.paymentMode}
                  onChange={handleFilterChange}
                  className="student-fee-filter-input"
                >
                  <option value="">All</option>
                  <option value="Cash">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="Net Banking">Net Banking</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Debit Card">Debit Card</option>
                  <option value="Cheque">Cheque</option>
                  <option value="DD">Demand Draft</option>
                </select>
              </label>
            </div>

            <div className="student-fee-filter-group">
              <label className="student-fee-filter-label">
                Degree
                <input
                  type="text"
                  name="degree"
                  value={filters.degree}
                  onChange={handleFilterChange}
                  className="student-fee-filter-input"
                  placeholder="Enter degree"
                />
              </label>
            </div>

            <div className="student-fee-filter-actions">
              <button type="submit" className="student-fee-apply-filter">Apply Filters</button>
              <button type="button" onClick={resetFilters} className="student-fee-reset-filter">Reset</button>
            </div>
          </form>
        </div>
      )}

      {/* Main Content */}
      <div className="student-fee-content">
        {loading ? (
            <>
            <Loader />
            <div className="student-fee-loading">Loading transactions...</div>
            </>
              
        ) : error ? (
          <div className="student-fee-error">{error}</div>
        ) : receipts.length === 0 ? (
          <div className="student-fee-no-data">No transactions found</div>
        ) : (
          <>
            <div className="student-fee-table-container">
              <table className="student-fee-table">
                <thead>
                  <tr>
                    <th className="student-fee-table-header">Receipt No.</th>
                    <th className="student-fee-table-header">Date</th>
                    <th className="student-fee-table-header">Amount</th>
                    <th className="student-fee-table-header">Payment Mode</th>
                    <th className="student-fee-table-header">Financial Year</th>
                    <th className="student-fee-table-header">Download</th>
                  </tr>
                </thead>
                <tbody>
                  {receipts.map((receipt) => (
                    <tr key={receipt._id} className="student-fee-table-row">
                      <td className="student-fee-table-cell">{receipt.receiptNo}</td>
                      <td className="student-fee-table-cell">{formatDate(receipt.createdAt)}</td>
                      <td className="student-fee-table-cell student-fee-amount">
                        ₹{typeof receipt.totalAmount === 'number' 
                          ? receipt.totalAmount.toLocaleString() 
                          : parseFloat(receipt.totalAmount).toLocaleString()}
                      </td>
                      <td className="student-fee-table-cell">{receipt.paymentMode}</td>
                      <td className="student-fee-table-cell">{receipt.financialYear}</td>
                      <td className="student-fee-table-cell">
                        <button
                          className="student-fee-download-button"
                          onClick={() => downloadPdf(receipt.pdfUrl, receipt.studentName, receipt.receiptNo)}
                        >
                          <DownloadIcon className="student-fee-icon" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="student-fee-pagination">
                <button
                  onClick={() => changePage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="student-fee-pagination-button"
                >
                  Prev
                </button>
                {renderPagination()}
                <button
                  onClick={() => changePage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="student-fee-pagination-button"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default FeeTransaction;